/**
 * `data.delete_safety` (D2-R3; docs/foundation-audit/d2-delete-cascades-and-warnings.md §4c): how loud the delete
 * dialogs are. Noah 2026-10-13: start heavy (decision 34A), and only Admin / Super Admin may turn a warning down,
 * with a reason (35A). Every warning level is a switch here, never code.
 *
 *   hard_delete_typed_confirmation   the red hard-delete dialog asks for the record's name typed (off: the tick only)
 *   hard_delete_read_seconds         the forced read before Delete permanently (and Roll back) enable
 *   hard_delete_names_limit          how many affected records a hard-delete preview names per line
 *   rollback_typed_confirmation      the ingestion rollback asks for the job name typed; the commit requires it (D2-R8)
 *   child_remove_level               removing a link or child row: a confirm dialog, or a toast with Undo (D2-R4)
 *   inline_impact_chips              the counts beside Delete permanently / Archive before they are pressed (D2-R7)
 *   admin_settings_deletes_level     role, data-source and request-tracker note deletes: the red dialog, or a plain confirm (D2-R5)
 *   show_behind_the_scenes           bookkeeping rows in a hard-delete dialog: folded into one line, or left out (D2-R6)
 *
 * The server decides how loud a preview's dialog is: every delete preview answers `confirmation` resolved from
 * this group (API services/delete-plan/delete-confirmation.ts), so a viewer without settings access still gets
 * the right dialog. The client-side switches (child removals, chips) are read through GET /api/settings/delete-safety,
 * open to every signed-in user (Brokers included); no readPermission, because a read key would also show the group,
 * and Settings → App settings, to everyone who holds it.
 *
 * Who: editPermission feature:system_settings (production: Admin and Super Admin, who pass anyway); every save
 * needs a reason. Edited on Settings → App settings, with History. Defaults = the heavy start (§4c). No env fallback.
 */
import { z } from 'zod';
import { defineGroup } from '../define.js';
/** The names limit's floor: the preview's names per line before D2-R3. */
const HARD_DELETE_NAMES_MIN = 10;
const HARD_DELETE_NAMES_MAX = 500;
const HARD_DELETE_READ_SECONDS_MAX = 10;
export const dataDeleteSafety = defineGroup('data.delete_safety', {
    label: 'Delete safety',
    editPermission: 'feature:system_settings',
    requireReason: true,
    settings: {
        hard_delete_typed_confirmation: {
            type: z.boolean(),
            default: true,
            label: 'Type the record name to delete permanently',
            help: 'On: the red Delete permanently dialog asks for the record name typed (pasting is off). Off: the tick box and the forced read only.',
        },
        hard_delete_read_seconds: {
            type: z.number().int().min(0).max(HARD_DELETE_READ_SECONDS_MAX),
            default: 5,
            label: 'Forced read before deleting (seconds)',
            help: 'How long Delete permanently (and Roll back on an import) stays off after the dialog opens, so the list of what goes is read first. 0 = no wait.',
        },
        hard_delete_names_limit: {
            type: z.number().int().min(HARD_DELETE_NAMES_MIN).max(HARD_DELETE_NAMES_MAX),
            default: HARD_DELETE_NAMES_MAX,
            label: 'Records named per line (hard delete)',
            help: 'How many affected records the Delete permanently dialog names on each line (spaces, sales, reports …); the rest are counted. Reports are always all named.',
        },
        rollback_typed_confirmation: {
            type: z.boolean(),
            default: true,
            label: 'Type the job name to roll back an import',
            help: 'On: rolling back an ingestion job asks for the job name typed.',
        },
        child_remove_level: {
            type: z.enum(['confirm', 'undo']),
            default: 'confirm',
            label: 'Removing a contact, party, broker, tag, photo …',
            help: 'confirm: a dialog names what is removed and what stays. undo: it is removed at once, with an Undo toast.',
        },
        inline_impact_chips: {
            type: z.boolean(),
            default: true,
            label: 'Show what a delete or archive affects before it is pressed',
            help: 'Counts beside Delete permanently and Archive (e.g. "2 sales · in 1 report"), from the same preview the dialog shows.',
        },
        admin_settings_deletes_level: {
            type: z.enum(['destructive', 'confirm']),
            default: 'destructive',
            label: 'Deleting a role, a data source or a request-tracker note',
            help: 'destructive: the red dialog with everything it affects, typed confirmation and a forced read. confirm: a plain confirm dialog.',
        },
        show_behind_the_scenes: {
            type: z.enum(['collapsed', 'hidden']),
            default: 'collapsed',
            label: 'Behind-the-scenes records in a delete dialog',
            help: 'collapsed: bookkeeping rows (provenance, chosen values, review runs …) are folded into one line you can open. hidden: they are not listed.',
        },
    },
});
/**
 * `data.quality` (CF-15; config-plan §1d, decision 26): the soft plausibility limits an import's approve checks
 * (API utils/rate-plausibility.ts, F-010). A value above a limit is kept, flagged with a warning on the run and
 * held as unreviewed until a reviewer signs it off; nothing is refused. Constants until CF-15, so no env fallback.
 * The defaults are exactly the old constants ("don't tighten without product sign-off": the May-11 $131k/SF row).
 * Who: feature:system_settings, a reason on every save.
 */
export const dataQuality = defineGroup('data.quality', {
    label: 'Import plausibility limits',
    editPermission: 'feature:system_settings',
    requireReason: true,
    settings: {
        max_lease_rate_per_sf_year: {
            type: z.number().positive().max(100_000),
            default: 500,
            label: 'Highest plausible lease rate ($/SF/year)',
            help: 'An imported lease base, asking or effective rate above this is flagged for review (usually the annual rent total read as a rate).',
        },
        max_sale_price_per_sf: {
            type: z.number().positive().max(1_000_000),
            default: 5000,
            label: 'Highest plausible sale price ($/SF)',
            help: 'An imported sale price per SF above this is flagged for review (usually the total price read as a per-SF figure).',
        },
        max_cap_rate_pct: {
            type: z.number().positive().max(100),
            default: 25,
            label: 'Highest plausible cap rate (%)',
            help: 'An imported cap rate above this percentage is flagged for review (usually a units or decimal-point error).',
        },
    },
});
/**
 * `data.freshness` (CF-15; config-plan §1d, decision 26): the Ops dashboard's freshness bands (API
 * routes/custom/ops-dashboard-routes.ts: /freshness, /freshness-trend, /top-actions). A property last touched
 * fewer than fresh_days ago is fresh, fewer than stale_days ago aging, else stale; Top actions lists only records
 * past fresh_days. Constants until CF-15 (30 / 90), so no env fallback. Who: feature:system_settings, with a reason.
 */
export const dataFreshness = defineGroup('data.freshness', {
    label: 'Data freshness',
    editPermission: 'feature:system_settings',
    requireReason: true,
    settings: {
        fresh_days: {
            type: z.number().int().min(1).max(3650),
            default: 30,
            label: 'Fresh for (days)',
            help: 'A property touched within this many days counts as fresh on the Ops dashboard, and is left out of Top actions.',
        },
        stale_days: {
            type: z.number().int().min(2).max(3650),
            default: 90,
            label: 'Stale after (days)',
            help: 'A property untouched for this many days or more counts as stale; between the two it is aging.',
        },
    },
    validate: (v) => (v.fresh_days < v.stale_days ? null : { code: 'FRESHNESS_BANDS_ORDER', message: 'Fresh for must be fewer days than Stale after.' }),
});
//# sourceMappingURL=data.js.map